import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import quotesIcon from "../../Assets/icons/quotes.svg";
import microphoneIcon from "../../Assets/icons/microphone.svg";
import StoryProgress from "../../Components/Gift/StoryProgress";
import AudioVisualizer from "../../Components/Gift/AudioVisualizer";
import useStoryTimer from "../../hooks/useStoryTimer";

// Scribble Circle Component
const ScribbleCircle = ({ color }) => (
  <motion.svg
    className="absolute -inset-4 w-[120%] h-[160%] pointer-events-none"
    viewBox="0 0 200 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ left: "-10%", top: "-30%" }}
  >
    <motion.path
      d="M20,50 C20,25 50,10 100,10 C150,10 180,25 180,50 C180,75 150,90 100,90 C50,90 20,75 20,50 C20,40 30,30 45,25"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{
        duration: 0.8,
        ease: "easeInOut",
        delay: 1,
      }}
    />
  </motion.svg>
);

// Scribble Underline Component
const ScribbleUnderline = ({ color = "#fff" }) => (
  <motion.svg
    className="absolute -bottom-4 left-0 w-full h-8 pointer-events-none"
    viewBox="0 0 200 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="none"
  >
    <motion.path
      d="M5,15 Q50,5 100,12 T195,10"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{
        duration: 0.8,
        ease: "easeInOut",
        delay: 0.8,
      }}
    />
  </motion.svg>
);

// Background music from S3
const bgMusic1 =
  "https://sayhelloo-files-672322547979.s3.amazonaws.com/assets/Christmas1.mp3";
const bgMusic2 =
  "https://sayhelloo-files-672322547979.s3.amazonaws.com/assets/Christmas2.mp3";
const bgMusic3 =
  "https://sayhelloo-files-672322547979.s3.amazonaws.com/assets/Christmas3.mp3";

// Title Slide Component
const TitleSlide = React.forwardRef(
  ({ recipient, sender, onOpen, hasStarted, occasion }, ref) => {
    // Helper for smart occasion text
    const getOccasionTitle = (occ) => {
      if (!occ) return "A Little Something...";
      const lowerOcc = occ.toLowerCase();
      
      // Standard mappings
      if (lowerOcc === "birthday") return "Happy Birthday!";
      if (lowerOcc === "anniversary") return "Happy Anniversary!";
      if (lowerOcc === "wedding") return "Happy Wedding!";
      if (lowerOcc === "holiday") return "Happy Holidays!";
      if (lowerOcc === "christmas") return "Merry Christmas!";
      if (lowerOcc.includes("thank you")) return "Thank You!";
      if (lowerOcc.includes("just because")) return "Just for You";

      // Custom occasion - display as is
      return occ;
    };

    return (
      <div
        ref={ref}
        className="w-full h-full flex flex-col items-center justify-center bg-[#050505] text-white p-8 relative overflow-hidden"
      >
        {/* Warm Spotlight Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-900/20 via-[#050505] to-[#050505] pointer-events-none" />

        {/* Floating Dust Motes - Optimized */}
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            y: ["-2%", "2%"],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
          style={{
            backgroundImage:
              "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "50px 50px",
            willChange: "transform",
          }}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-center flex flex-col items-center relative z-10 mb-40"
        >
          <div className="relative">
            <motion.h1
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
              className="text-6xl md:text-7xl font-serif font-bold mb-6 tracking-tight text-white relative z-10"
            >
              For {recipient}
            </motion.h1>
            <ScribbleUnderline />
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="text-xl text-gray-300 font-light italic tracking-wide mt-4"
          >
            From {sender}
          </motion.p>
        </motion.div>

        {/* Bottom Drawer Intro */}
        {!hasStarted && (
          <motion.div
            className="absolute bottom-0 left-0 w-full bg-[#fdfbf7] text-gray-900 rounded-t-[2.5rem] p-8 pb-12 z-50 shadow-[0_-20px_60px_rgba(0,0,0,0.7)] flex flex-col items-center"
            initial={{ y: "100%" }}
            animate={{ y: "0%" }}
            transition={{
              delay: 2.5,
              type: "spring",
              damping: 25,
              stiffness: 100,
            }}
          >
            {/* Drag Handle Indicator */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mb-8"></div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.7, duration: 0.5 }}
              className="text-center w-full"
            >
              <h3 className="text-3xl font-serif font-semibold mb-3 text-gray-900">
                {getOccasionTitle(occasion)}
              </h3>

              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                className="w-full bg-black text-white py-5 mt-10 rounded-2xl text-xl font-medium tracking-wide shadow-xl flex items-center justify-center gap-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Open Gift</span>
                {/* Gift Icon SVG */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
                  />
                </svg>
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </div>
    );
  }
);

// Ending Slide Component
const EndingSlide = ({ endingMessage }) => {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-black text-white p-8 relative">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="text-center flex flex-col items-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-2xl font-light text-gray-400 mb-6 font-serif italic"
        >
          In short,
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 1, ease: "easeOut" }}
          className="text-5xl md:text-7xl font-serif font-light mb-8 leading-tight"
        >
          {endingMessage}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, duration: 0.5, type: "spring" }}
          className="text-4xl"
        >
          <ScribbleCircle />
        </motion.div>
      </motion.div>
    </div>
  );
};

const GiftFeed = () => {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay on iOS
  const bgMusicRef = useRef(new Audio());
  const activeVideoRef = useRef(null);

  // Effective paused state (either button toggle or holding screen)
  const effectivePaused = isPaused || isHolding;

  // Enter fullscreen and prevent scrolling
  useEffect(() => {
    // Hide body overflow
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // Request fullscreen
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          await document.documentElement.webkitRequestFullscreen();
        }
      } catch (err) {
        console.log("Fullscreen not supported or denied:", err);
      }
    };

    enterFullscreen();

    return () => {
      // Restore scrolling on unmount
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";

      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    };
  }, []);

  useEffect(() => {
    fetchData();
    return () => {
      bgMusicRef.current.pause();
    };
  }, [giftId]);

  const fetchData = async () => {
    try {
      // 1. Get Session
      const sessionRes = await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_gift_session`,
        {
          method: "POST",
          body: JSON.stringify({ giftId }),
        }
      );
      const sessionData = await sessionRes.json();
      
      if (!sessionData || sessionData.error) {
        console.error("Gift session not found");
        setLoading(false);
        return;
      }

      setSession(sessionData);

      // Use the actual giftId from the session for subsequent calls
      // This ensures we work correctly even if the URL parameter was a cardId
      const realGiftId = sessionData.giftId;

      // 2. Get Messages
      const messagesRes = await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_gift_messages`,
        {
          method: "POST",
          body: JSON.stringify({ giftId: realGiftId }),
        }
      );
      const giftMessages = await messagesRes.json();

      const allMessages = [...giftMessages];

      // Inject Title Slide
      const titleSlide = {
        type: "title",
        _id: "title-slide",
        recipient: sessionData.recipientName,
        sender: sessionData.senderName,
      };

      // Inject Ending Slide
      const endingSlide = {
        type: "ending",
        _id: "ending-slide",
        endingMessage: sessionData.endingMessage || "You are awesome!",
      };

      setMessages([titleSlide, ...allMessages, endingSlide]);

      // Preload all media assets before showing animations
      await preloadAssets([titleSlide, ...allMessages, endingSlide]);
      setAssetsLoaded(true);
    } catch (error) {
      console.error("Error fetching feed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Preload images and videos to prevent lag
  const preloadAssets = async (msgs) => {
    const loadPromises = [];

    msgs.forEach((msg) => {
      if (msg.type === "image" && msg.contentUrl) {
        const img = new Image();
        const promise = new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // Continue even if image fails
          img.src = msg.contentUrl;
        });
        loadPromises.push(promise);
      } else if (msg.type === "video" && msg.contentUrl) {
        const video = document.createElement("video");
        video.preload = "metadata";
        const promise = new Promise((resolve) => {
          video.onloadedmetadata = resolve;
          video.onerror = resolve; // Continue even if video fails
          video.src = msg.contentUrl;
        });
        loadPromises.push(promise);
      } else if (msg.type === "audio" && msg.contentUrl) {
        const audio = new Audio();
        audio.preload = "metadata";
        const promise = new Promise((resolve) => {
          audio.onloadedmetadata = resolve;
          audio.onerror = resolve; // Continue even if audio fails
          audio.src = msg.contentUrl;
        });
        loadPromises.push(promise);
      }
    });

    // Wait for all assets to load (with timeout)
    await Promise.race([
      Promise.all(loadPromises),
      new Promise((resolve) => setTimeout(resolve, 3000)), // Max 3s wait
    ]);
  };

  // Toggle pause/play
  const togglePause = (e) => {
    e.stopPropagation(); // Prevent triggering hold handlers
    setIsPaused(prev => !prev);
  };

  // Toggle mute/unmute
  const toggleMute = (e) => {
    e.stopPropagation(); // Prevent triggering hold handlers
    setIsMuted(prev => !prev);
  };

  // Pause/resume background music based on effectivePaused state
  useEffect(() => {
    if (!bgMusicRef.current) return;
    
    if (effectivePaused) {
      bgMusicRef.current.pause();
    } else if (hasStarted) {
      bgMusicRef.current.play().catch(e => console.log("Resume music failed", e));
    }
  }, [effectivePaused, hasStarted]);

  // Pause/resume active video based on effectivePaused state
  useEffect(() => {
    if (activeVideoRef.current) {
      if (effectivePaused) {
        activeVideoRef.current.pause();
      } else {
        activeVideoRef.current.play().catch(e => console.log("Resume video failed", e));
      }
    }
  }, [effectivePaused]);

  const startExperience = () => {
    setHasStarted(true);
    // Play background music
    const musics = [bgMusic1, bgMusic2, bgMusic3];
    const randomMusic = musics[Math.floor(Math.random() * musics.length)];
    bgMusicRef.current.src = randomMusic;
    bgMusicRef.current.volume = 0.1;
    bgMusicRef.current.loop = true;
    bgMusicRef.current.play().catch((e) => console.log("Audio play failed", e));
  };

  const handleNext = () => {
    if (currentIndex < messages.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const currentMessage = messages[currentIndex];
  const [mediaProgress, setMediaProgress] = useState(0);

  const handleRestart = () => {
    setCurrentIndex(0);
    setHasStarted(false);
    setMediaProgress(0);
    // Reset timer if needed
    if (resetKey) {
      setResetKey((prev) => prev + 1);
    }
  };

  // Determine if current slide is static or media
  const isStatic =
    currentMessage &&
    ["title", "text", "image", "ending"].includes(currentMessage.type);

  // Calculate duration for static slides
  const staticDuration = isStatic
    ? currentMessage.type === "title"
      ? 5000
      : currentMessage.type === "ending"
      ? 5000
      : currentMessage.type === "image"
      ? 5000
      : Math.max(6000, (currentMessage.textContent?.length || 0) * 100)
    : 0;

  // Use the robust timer hook for static slides
  // Title slide: activate timer only after user clicks (hasStarted)
  // Other static slides: activate when hasStarted is true
  const shouldActivateTimer =
    isStatic && (currentMessage.type === "title" ? hasStarted : hasStarted);

  const staticProgress = useStoryTimer(
    staticDuration,
    shouldActivateTimer,
    handleNext,
    currentMessage?._id, // Reset when message changes
    effectivePaused // Pass effective paused state
  );

  // Unified progress value
  const progress = isStatic ? staticProgress : mediaProgress;

  // Reset media progress when slide changes
  useEffect(() => {
    setMediaProgress(0);
  }, [currentIndex]);

  if (loading || !assetsLoaded) {
    return (
      <div className="bg-black h-screen w-full flex items-center justify-center text-white font-serif">
        <div className="text-center">
          <div className="mb-4">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
          <p className="text-lg">Loading your gift...</p>
        </div>
      </div>
    );
  }

  if (!loading && messages.length === 0) {
    return (
      <div className="bg-black h-screen w-full flex items-center justify-center text-white font-serif">
        <div className="text-center">
          <p className="text-lg">Gift not found or no messages available.</p>
        </div>
      </div>
    );
  }

  // Safety check
  if (!currentMessage) return null;

  return (
    <div 
      className="fixed inset-0 w-full h-screen bg-black overflow-hidden select-none touch-none"
      onPointerDown={() => setIsHolding(true)}
      onPointerUp={() => setIsHolding(false)}
      onPointerLeave={() => setIsHolding(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Story Progress Bars */}
      {!loading && messages.length > 0 && (
        <StoryProgress
          totalSteps={messages.length}
          currentStep={currentIndex}
          progress={progress}
        />
      )}

      {/* Pause/Play Button */}
      {hasStarted && currentMessage.type !== "title" && currentMessage.type !== "ending" && (
        <button
          onClick={togglePause}
          className="absolute top-16 left-4 z-50 bg-black/30 backdrop-blur-md p-3 rounded-full hover:bg-black/50 transition-colors"
          aria-label={isPaused ? "Play" : "Pause"}
        >
          {isPaused ? (
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          )}
        </button>
      )}

      {/* Mute/Unmute Button - Only show for video messages */}
      {hasStarted && currentMessage.type === "video" && (
        <button
          onClick={toggleMute}
          className="absolute top-32 left-4 z-50 bg-black/30 backdrop-blur-md p-3 rounded-full hover:bg-black/50 transition-colors"
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
      )}

      <AnimatePresence mode="popLayout" initial={false}>
        {currentMessage.type === "title" && (
          <TitleSlide
            key="title"
            recipient={session?.recipientName}
            sender={session?.senderName}
            onOpen={startExperience}
            hasStarted={hasStarted}
            occasion={session?.occasion}
          />
        )}

        {currentMessage.type === "ending" && (
          <EndingSlide
            key="ending"
            endingMessage={currentMessage.endingMessage}
            onRestart={handleRestart}
          />
        )}

        {currentMessage.type === "video" && (
          <motion.div
            key={currentMessage._id}
            className="w-full h-full bg-black flex items-center justify-center relative"
            initial={{ y: "100%" }}
            animate={{
              y: "0%",
              transition: currentIndex % 2 !== 0 
                ? { type: "spring", stiffness: 300, damping: 30 }
                : { duration: 0 },
            }}
            exit={{
              y: "-100%",
              transition: { duration: 0.5, ease: "easeInOut" },
            }}
          >
            {currentIndex % 2 !== 0 ? (
              /* Odd: Cinematic */
              <>
                {/* Blurred Background */}
                <div className="absolute inset-0 overflow-hidden opacity-30">
                  <video
                    src={currentMessage.contentUrl}
                    className="w-full h-full object-cover blur-xl scale-110"
                    muted
                  />
                </div>

                <video
                  ref={activeVideoRef}
                  src={currentMessage.contentUrl}
                  className="relative z-10 w-full h-full max-w-md object-cover md:object-contain shadow-2xl"
                  autoPlay
                  playsInline
                  muted={false}
                  onEnded={handleNext}
                  onTimeUpdate={(e) => {
                    if (e.target.duration) {
                      setMediaProgress(
                        (e.target.currentTime / e.target.duration) * 100
                      );
                    }
                  }}
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60 z-20 pointer-events-none"></div>

                {/* Caption - Bottom Left */}
                <motion.div
                  className="absolute bottom-12 left-0 w-full p-8 z-30 text-left pointer-events-none"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <p className="text-white font-crimson text-xl font-normal mb-1">
                    {currentMessage.senderName}
                  </p>
                  {currentMessage.textContent && (
                    <p className="text-white/80 text-sm font-light">
                      {currentMessage.textContent}
                    </p>
                  )}
                </motion.div>
              </>
            ) : (
              /* Even: Camera Recording */
              <>
                {/* Blurred Background */}
                <div className="absolute inset-0 overflow-hidden opacity-30">
                  <video
                    src={currentMessage.contentUrl}
                    className="w-full h-full object-cover blur-xl scale-110"
                    muted
                  />
                </div>

                <video
                  ref={(video) => {
                    // Combine refs: update activeVideoRef and add event listener
                    activeVideoRef.current = video;
                    if (video) {
                      video.addEventListener('timeupdate', (e) => {
                        if (e.target.duration) {
                          setMediaProgress(
                            (e.target.currentTime / e.target.duration) * 100
                          );
                          // Update timer display
                          const minutes = Math.floor(e.target.currentTime / 60);
                          const seconds = Math.floor(e.target.currentTime % 60);
                          const timerEl = document.getElementById(`timer-${currentMessage._id}`);
                          if (timerEl) {
                            timerEl.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                          }
                        }
                      });
                    }
                  }}
                  src={currentMessage.contentUrl}
                  className="relative z-10 w-full h-full max-w-md object-cover md:object-contain shadow-2xl"
                  autoPlay
                  playsInline
                  muted={isMuted}
                  onEnded={handleNext}
                />

                {/* Camera Recording UI - Top Right */}
                <div className="absolute top-6 right-4 z-30 flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                  {/* Pulsing Red Dot */}
                  <motion.div
                    className="w-2 h-2 bg-red-600 rounded-full"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  
                  {/* REC Text */}
                  <span className="text-white font-mono text-xs font-semibold tracking-wider">REC</span>
                  
                  {/* Timer */}
                  <span id={`timer-${currentMessage._id}`} className="text-white font-mono text-xs font-medium">
                    00:00
                  </span>
                </div>

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60 z-20 pointer-events-none"></div>

                {/* Caption - Bottom Left */}
                <motion.div
                  className="absolute bottom-12 left-0 w-full p-8 z-30 text-left pointer-events-none"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <p className="text-white font-crimson text-xl font-normal mb-1">
                    {currentMessage.senderName}
                  </p>
                  {currentMessage.textContent && (
                    <p className="text-white/80 text-sm font-light">
                      {currentMessage.textContent}
                    </p>
                  )}
                </motion.div>
              </>
            )}
          </motion.div>
        )}

        {currentMessage.type === "image" && (
          <div
            key={currentMessage._id}
            className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden bg-black"
          >
            {currentIndex % 2 !== 0 ? (
              /* Odd: Camera Shutter/Flash (Current Style) */
              <>
                {/* Blurred Background */}
                <div className="absolute inset-0 overflow-hidden opacity-30">
                  <img
                    src={currentMessage.contentUrl}
                    className="w-full h-full object-cover blur-xl scale-110"
                    alt="Background"
                  />
                </div>

                {/* Main Image - Brightness flash with shutter */}
                <motion.img
                  src={currentMessage.contentUrl}
                  className="w-full h-full object-contain relative z-10"
                  alt="Gift"
                  animate={{
                    filter: ["brightness(1)", "brightness(1.8)", "brightness(1)"],
                  }}
                  transition={{
                    duration: 0.3,
                    times: [0, 0.4, 1],
                    ease: "easeOut",
                  }}
                />

                {/* Camera Shutter Effect - Top Blade */}
                <motion.div
                  className="absolute top-0 left-0 w-full h-1/2 bg-black z-40"
                  initial={{ y: 0 }}
                  animate={{ y: "-100%" }}
                  transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
                />

                {/* Camera Shutter Effect - Bottom Blade */}
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-1/2 bg-black z-40"
                  initial={{ y: 0 }}
                  animate={{ y: "100%" }}
                  transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
                />

                {/* Camera Flash Effect */}
                <motion.div
                  className="absolute inset-0 bg-white z-30"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.9, 0] }}
                  transition={{ duration: 0.15, times: [0, 0.5, 1] }}
                />

                {/* Cinematic Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60 z-20"></div>

                {/* Caption/Sender - Bottom Left */}
                <motion.div
                  className="absolute bottom-12 left-0 w-full p-8 z-30 text-left"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <p className="text-white font-crimson text-xl font-normal mb-1">
                    {currentMessage.senderName}
                  </p>
                  {currentMessage.textContent && (
                    <p className="text-white/80 text-sm font-light">
                      {currentMessage.textContent}
                    </p>
                  )}
                </motion.div>
              </>
            ) : (
              /* Even: Polaroid Frame */
              <>
                {/* Random gradient background */}
                <motion.div 
                  className={`absolute inset-0 ${
                  [
                    'bg-gradient-to-br from-violet-950 via-fuchsia-700 to-rose-900',
                    'bg-gradient-to-br from-amber-950 via-yellow-700 to-orange-900',
                    'bg-gradient-to-br from-red-950 via-rose-700 to-pink-900',
                    'bg-gradient-to-br from-emerald-950 via-green-700 to-teal-900',
                    'bg-gradient-to-br from-sky-950 via-blue-700 to-cyan-900',
                    'bg-gradient-to-br from-purple-950 via-violet-700 to-indigo-900',
                  ][currentIndex % 6]
                }`}
                  animate={{
                    backgroundPosition: ["0% 0%", "100% 100%"],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "linear",
                  }}
                  style={{ backgroundSize: "200% 200%" }}
                ></motion.div>

                {/* Polaroid Container */}
                <motion.div
                  className="relative z-10 bg-white rounded-sm shadow-2xl max-w-sm w-full"
                  initial={{ scale: 0.9, rotate: -2 }}
                  animate={{ scale: 1, rotate: 1 }}
                  transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
                >
                  {/* Tape effect at top */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-20 h-8 bg-white/40 backdrop-blur-sm rotate-1 rounded-sm shadow-md z-20"></div>

                  {/* Image Frame */}
                  <div className="p-4 pb-20">
                    <img
                      src={currentMessage.contentUrl}
                      className="w-full h-auto max-h-[60vh] object-contain rounded-sm"
                      alt="Gift"
                    />
                  </div>

                  {/* Polaroid Caption Area (White Space Below) */}
                  <motion.div
                    className="absolute bottom-4 left-4 right-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <p className="text-gray-800 font-garamond text-lg italic text-center mb-1">
                      {currentMessage.senderName}
                    </p>
                    {currentMessage.textContent && (
                      <p className="text-gray-600 text-xs text-center font-light">
                        {currentMessage.textContent}
                      </p>
                    )}
                  </motion.div>
                </motion.div>
              </>
            )}
          </div>
        )}

        {currentMessage.type === "audio" && (
          <motion.div
            key={currentMessage._id}
            className="w-full h-full flex flex-col items-center justify-center text-white p-8 relative overflow-hidden"
            initial={{ y: "100%" }}
            animate={{
              y: "0%",
              transition: { type: "spring", stiffness: 300, damping: 30 },
            }}
            exit={{
              y: "-100%",
              transition: { duration: 0.5, ease: "easeInOut" },
            }}
          >
            {/* Alternating Backgrounds */}
            {currentIndex % 2 !== 0 ? (
              /* Odd: Concentric Blue Ripples - Audio Reactive */
              <div className="absolute inset-0 bg-[#0ea5e9] flex items-center justify-center overflow-hidden">
                {[
                  { size: "180vmax", opacity: 0.03, reactivity: 0.15, duration: 400 },
                  { size: "150vmax", opacity: 0.05, reactivity: 0.25, duration: 300 },
                  { size: "120vmax", opacity: 0.07, reactivity: 0.35, duration: 250 },
                  { size: "90vmax", opacity: 0.09, reactivity: 0.5, duration: 200 },
                  { size: "60vmax", opacity: 0.11, reactivity: 0.7, duration: 150 },
                  { size: "30vmax", opacity: 0.13, reactivity: 1.0, duration: 100 },
                ].map((circle, i) => (
                  <div
                    key={i}
                    id={`audio-circle-${i}`}
                    className="absolute rounded-full bg-white"
                    style={{
                      width: circle.size,
                      height: circle.size,
                      opacity: circle.opacity,
                      transform: 'scale(1)',
                      transition: `transform ${circle.duration}ms ease-out`,
                    }}
                  />
                ))}
                {/* Vignette Overlay for depth */}
                <div className="absolute inset-0 bg-radial-gradient from-transparent to-black/20 pointer-events-none"></div>
              </div>
            ) : (
              /* Even: Minimal Moving Gradient (Alternating Colors) */
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${
                  currentIndex % 4 === 0
                    ? "from-slate-950 via-gray-900 to-zinc-900" // Monochrome
                    : "from-neutral-950 via-stone-900 to-neutral-900" // Earthy
                }`}
                animate={{
                  backgroundPosition: ["0% 0%", "100% 100%"],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "linear",
                }}
                style={{ backgroundSize: "200% 200%" }}
              />
            )}

            {/* Center Content: Visualizer */}
            <div className="relative z-10 flex flex-col items-center justify-center w-full h-full max-w-md">
              <AudioVisualizer
                src={currentMessage.contentUrl}
                onEnded={handleNext}
                onTimeUpdate={(currentTime, duration) => {
                  if (duration) {
                    setMediaProgress((currentTime / duration) * 100);
                  }
                }}
                variant={currentIndex % 2 !== 0 ? "microphone" : "bars"}
                icon={microphoneIcon}
                paused={effectivePaused}
              />
            </div>

            {/* Bottom Content: Name & Caption */}
            <motion.div
              className="absolute bottom-12 left-0 w-full p-8 z-30 text-center pointer-events-none"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <p className="text-white font-crimson text-2xl font-normal mb-2">
                {currentMessage.senderName}
              </p>
              {currentMessage.textContent && (
                <p className="text-white/80 text-sm font-light max-w-xs mx-auto">
                  {currentMessage.textContent}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}

        {currentMessage.type === "text" && (
          <motion.div
            key={currentMessage._id}
            className={`w-full h-full flex flex-col items-center justify-center p-8 md:p-12 relative overflow-hidden ${
              messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0
                ? "bg-[#fdfbf7] text-gray-900" // White Theme
                : "bg-[#1a1a1a] text-gray-100" // Black Theme
            }`}
            initial={{ y: "100%" }}
            animate={{
              y: "0%",
              transition: { type: "spring", stiffness: 300, damping: 30 },
            }}
            exit={{
              y: "-100%", // Slide up to reveal next
              transition: { duration: 0.6, ease: "easeInOut" },
            }}
          >
            {/* Floating Golden Embers/Particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute rounded-full blur-[1px] ${
                  messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 ? "bg-amber-400/20" : "bg-amber-200/20"
                }`}
                style={{
                  width: Math.random() * 6 + 2 + "px",
                  height: Math.random() * 6 + 2 + "px",
                  left: Math.random() * 100 + "%",
                  top: Math.random() * 100 + "%",
                }}
                animate={{
                  y: [0, -30, 0],
                  x: [0, Math.random() * 20 - 10, 0],
                  opacity: [0.2, 0.5, 0.2],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: Math.random() * 5 + 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 2,
                }}
              />
            ))}

            {/* Noise Texture (only for white backgrounds) */}
            {messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 && (
              <div 
                className="absolute inset-0 opacity-[0.05] pointer-events-none z-10"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                }}
              />
            )}

            {/* Subtle Gradient Overlay (Warm for White, Cool for Black) */}
            <div
              className={`absolute inset-0 bg-gradient-to-b pointer-events-none ${
                messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0
                  ? "from-orange-50/10 via-transparent to-rose-50/20"
                  : "from-blue-900/10 via-transparent to-purple-900/10"
              }`}
            ></div>

            {/* Large Name Watermark (only for white backgrounds) */}
            {messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 && (
              <>
                {/* Top Watermark */}
                <div className="absolute top-0 left-0 right-0 overflow-hidden pointer-events-none z-10 flex justify-end">
                  <p className="text-gray-300/20 font-bhutuka text-[12rem] md:text-[16rem] leading-none whitespace-nowrap -mt-16">
                    {currentMessage.senderName}
                  </p>
                </div>
                
                {/* Bottom Watermark */}
                <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none z-10">
                  <p className="text-gray-300/20 font-bhutuka text-[12rem] md:text-[16rem] leading-none whitespace-nowrap -mb-16">
                    {currentMessage.senderName}
                  </p>
                </div>
              </>
            )}

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
              className="max-w-xl w-full relative z-10 px-4"
            >
              {/* Quote Icon */}
              <div
                className={`mb-6 ${
                  messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 ? "opacity-40" : "opacity-30 invert"
                }`}
              >
                <img src={quotesIcon} alt="Quote" className="w-8 h-8" />
              </div>

              {/* Main Text */}
              <p
                className={`text-2xl md:text-3xl font-garamond leading-relaxed mb-8 ${
                  messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 ? "text-gray-800" : "text-gray-200"
                }`}
              >
                {currentMessage.textContent}
              </p>

              {/* Sender Name - Right Aligned */}
              <motion.div
                className="flex justify-end relative"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <div className="relative">
                  <p
                    className={`text-lg font-garamond italic relative z-10 ${
                      messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    — {currentMessage.senderName}
                  </p>
                  <ScribbleCircle
                    color={messages.slice(0, currentIndex).filter(m => m.type === 'text').length % 2 === 0 ? "#d97706" : "#fcd34d"}
                  />
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Controls (Tap areas) - Only active after gift is opened */}
      {hasStarted && (
        <>
          <div
            className="absolute inset-y-0 left-0 w-[30%] z-40 cursor-w-resize"
            onClick={handlePrev}
          ></div>
          <div
            className="absolute inset-y-0 right-0 w-[70%] z-40 cursor-e-resize"
            onClick={handleNext}
          ></div>
        </>
      )}
    </div>
  );
};

export default GiftFeed;
